import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Statistic } from "@/components/ui/statistic"
import { formatCurrency } from "@/lib/utils"
import type { Agent } from "@/types"

type Timeframe = "24_hours" | "7_days" | "30_days" | "3_months" | "12_months"

const TIMEFRAME_OPTIONS: { value: Timeframe; label: string }[] = [
  { value: "24_hours", label: "Last 24 hours" },
  { value: "7_days", label: "Last 7 days" },
  { value: "30_days", label: "Last 30 days" },
  { value: "3_months", label: "Last 3 months" },
  { value: "12_months", label: "Last 12 months" },
]

interface ImpactSummaryProps {
  agent: Agent
  timeframe: Timeframe
  onTimeframeChange: (timeframe: Timeframe) => void
}

export default function ImpactSummary({
  agent,
  timeframe,
  onTimeframeChange,
}: ImpactSummaryProps) {
  const impactSummary = agent.impact_summary || {
    hours_saved: 0,
    time_value_cents: 0,
    metrics: {},
  }

  const statistics = [
    {
      label: "Hours saved",
      value: Math.round(impactSummary.hours_saved),
      tooltip:
        "Estimated hours of manual work avoided by running agents during the selected period.",
    },
    {
      label: "Time value",
      value: formatCurrency(impactSummary.time_value_cents / 100),
      tooltip:
        "Estimated monetary value of manual work avoided during the selected period.",
    },
  ]

  // Add custom metrics to statistics
  if (agent.agent_metrics && impactSummary.metrics) {
    agent.agent_metrics.forEach((metric) => {
      const metricValue = impactSummary.metrics?.[metric.uid]
      if (metricValue !== undefined && metricValue !== null) {
        statistics.push({
          label: metric.label,
          value:
            typeof metricValue === "number"
              ? metricValue.toLocaleString(undefined, {
                  maximumFractionDigits: 2,
                })
              : String(metricValue),
          tooltip: metric.description || "",
        })
      }
    })
  }

  // Calculate optimal grid columns based on number of statistics
  const getGridCols = (count: number): string => {
    const gridColsMap: Record<number, string> = {
      1: "grid-cols-1",
      2: "grid-cols-2",
      3: "grid-cols-3",
      4: "grid-cols-4",
      5: "grid-cols-3",
      6: "grid-cols-3",
      7: "grid-cols-4",
      8: "grid-cols-4",
      9: "grid-cols-3",
      10: "grid-cols-4",
      11: "grid-cols-4",
      12: "grid-cols-4",
    }

    if (count <= 0) return "grid-cols-1"
    if (count in gridColsMap) return gridColsMap[count]

    return "grid-cols-4"
  }

  const handleTimeframeChange = (value: string) => {
    if (!value) return
    const timeframeValue = value as Timeframe
    onTimeframeChange(timeframeValue)
  }

  return (
    <div className="space-y-4">
      <div>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-semibold">Impact Summary</h2>
          <Select value={timeframe} onValueChange={handleTimeframeChange}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Select time period" />
            </SelectTrigger>
            <SelectContent>
              {TIMEFRAME_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className={`grid ${getGridCols(statistics.length)} gap-4`}>
          {statistics.map((stat, index) => (
            <Statistic
              key={index}
              label={stat.label}
              value={stat.value}
              tooltip={stat.tooltip}
            />
          ))}
        </div>
      </div>
    </div>
  )
}
